import { FC, useMemo } from "react";
import { BarChart, HighlightScope } from "@mui/x-charts";
import { KpiPersonal } from "../../../../Core/Api/Types/get-workflow-user-tasks-kpi-response";
import { ObjectType } from "../../../../Core/Api/Types/object-type";
import { GetAssetObjectReadableType, AssetTypeAlphaColor } from "../../../../Utils/helpers";
import { Box, Button, ButtonGroup, Stack, Typography } from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import { ArrowForward } from "@mui/icons-material";
import KpiTooltip from "./kpi-tooltip";
import { useTranslation } from "react-i18next";

var moment = require("moment");
require("moment-duration-format");

interface KpiChartsProps {
  data: KpiPersonal[];
  selectedUserId: number;
  setSelectedUserId: (userId: number) => void;
  startDate: string;
  endDate: string;
  stackDirection: "row" | "column";
  currentUserIndex: number;
  currentUserData: KpiPersonal | null;
}

const KpiCharts: FC<KpiChartsProps> = (props) => {
  const { t } = useTranslation();
  const {
    data,
    selectedUserId,
    startDate,
    endDate,
    stackDirection,
    setSelectedUserId,
    currentUserData,
    currentUserIndex,
  } = props;

  const chartConfiguration = useMemo(() => {
    const start = moment(startDate);
    const end = moment(endDate);
    const timeIntervalInDays = end.diff(start, "days");
    const timeData: Date[] = [];
    let series: any[] = Object.values(ObjectType).map((x) => ({
      name: x,
      data: [] as number[],
      stack: "A",
      label: GetAssetObjectReadableType(x),
      averageTaskTime: [] as string[],
      averageDelayTime: [] as string[],
      spentTimeTotal: [] as string[],
      color: AssetTypeAlphaColor(x, 1),
      currentDate: [] as string[],
      highlightScope: {
        highlighted: "item",
        faded: "global",
      } as HighlightScope,
    }));

    for (let i = 0; i < timeIntervalInDays; i++) {
      const currentDate = new Date(start.add(1, "d").format("YYYY-MM-DD"));
      timeData.push(currentDate);
      const foundData = currentUserData?.counters.find(
        (x) => moment(x.date).format("YYYY-MM-DD") === moment(currentDate).format("YYYY-MM-DD")
      );

      series.forEach((s, index) => {
        const foundCounter = foundData?.counters.find((x) => x.objectType === s.name);
        series[index] = {
          ...series[index],
          data: [...(series[index].data ?? []), foundCounter?.count ?? 0],
          averageTaskTime: [...(series[index].averageTaskTime ?? []), foundCounter?.averageTaskTime ?? "00:00:00"],
          averageDelayTime: [...(series[index].averageDelayTime ?? []), foundCounter?.averageDelayTime ?? "00:00:00"],
          spentTimeTotal: [...(series[index].spentTimeTotal ?? []), foundCounter?.spentTimeTotal ?? "00:00:00"],
          currentDate: [...(series[index].currentDate ?? []), moment(currentDate).format("DD/MM")],
        };
      });
    }

    const xAxis = {
      data: timeData,
      scaleType: "band" as "band",
      valueFormatter: (date: Date) =>
        date.toLocaleDateString("fr-FR", {
          month: "2-digit",
          day: "2-digit",
        }),
    };

    const config = {
      series: series.filter((x) => x.data.find((d) => d !== 0) !== undefined),
    };

    return {
      xAxis,
      config,
    };
  }, [startDate, endDate, currentUserData, stackDirection]);

  if (selectedUserId > 0 && currentUserData)
    return (
      <Box position={"relative"} width={"100%"} height={"100%"}>
        <Stack direction={"row"} spacing={2} sx={{ position: "absolute", top: 0, right: 0, padding: 2, zIndex: 799 }}>
          <Typography lineHeight={"40px"} variant="h6">
            {currentUserData.userName}
          </Typography>
          <ButtonGroup>
            <Button
              disabled={currentUserIndex <= 0}
              onClick={() => setSelectedUserId(data[currentUserIndex - 1].userId!)}
              color="neutral"
              variant="rounded"
            >
              <ArrowBack />
            </Button>
            <Button
              disabled={currentUserIndex > data.length - 2 || currentUserIndex === -1}
              onClick={() => setSelectedUserId(data[currentUserIndex + 1].userId!)}
              color="neutral"
              variant="rounded"
            >
              <ArrowForward />
            </Button>
          </ButtonGroup>
        </Stack>
        <BarChart
          tooltip={{ trigger: "item", itemContent: KpiTooltip }}
          xAxis={[chartConfiguration.xAxis]}
          {...chartConfiguration.config}
        />
      </Box>
    );

  return (
    <Box
      height={"100%"}
      width={"100%"}
      display={"flex"}
      justifyContent={"center"}
      alignItems={"center"}
      alignContent={"center"}
      textAlign={"center"}
      flexDirection={"column"}
    >
      <Typography variant="h5">{t("page:body.noDataToShow")}</Typography>
      <Typography color="text.tier" variant="body2">
        {t("page:body.selectUserInTable")}
      </Typography>
    </Box>
  );
};

export default KpiCharts;
